/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IButtonStyles, IconButton } from '@fluentui/react/lib/Button';
import { IIconProps } from '@fluentui/react/lib/Icon';
import { ISearchBoxProps } from '@fluentui/react/lib/SearchBox';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisDebouncedSearchBox } from 'src/core/components/LuisDebouncedSearchBox';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2 } from 'src/core/themes/luisStylist';

const messages = defineMessages({
    toggleSearchBox: {
        id: 'utterances.utteranceTableCommandBar.toggleSearchBox',
        defaultMessage: 'Search box'
    }
});

const { getClassName } = getStylistV2('CollapsableSearchBoxProps');

const iconRootClassName = getClassName('IconRoot', { height: '100%', width: 40, alignSelf: 'center', backgroundColor: 'transparent' });
const menuIconClassName = getClassName('MenuIcon', { fontSize: 16 });
const searchBoxRootClassName = getClassName('SearchBoxRoot', { alignSelf: 'center' });

export type CollapsableSearchBoxProps = ISearchBoxProps & {
    /**
     * Icons props applied to the icon.
     */
    menuIconProps?: IIconProps;

    /**
     * Styles applied to icon button.
     */
    buttonStyles?: IButtonStyles;

    /**
     * If set to true, the search box will always appear.
     */
    alwaysExpanded?: boolean;
};

export const CollapsableSearchBox = (props: CollapsableSearchBoxProps) => {
    const { value = '', menuIconProps, alwaysExpanded = false, buttonStyles, styles: searchBoxStyles } = props;
    const { localize } = useLocalization();
    const [isSearchBoxDisplayed, setIsSearchBoxDisplayed] = React.useState(value.length > 0);

    const handleSearchIconClick = () => setIsSearchBoxDisplayed(state => !state);

    const handleSearchBoxBlur: ISearchBoxProps['onBlur'] = e => {
        if (value.length === 0) {
            setIsSearchBoxDisplayed(false);
        }
        props.onBlur?.(e);
    };

    return isSearchBoxDisplayed || alwaysExpanded ? (
        <LuisDebouncedSearchBox
            autoFocus
            size={30}
            value={value}
            data-automation-id="collapsableSearchField"
            onBlur={handleSearchBoxBlur}
            {...props}
            styles={mergeStyleSets({ root: searchBoxRootClassName }, searchBoxStyles)}
        />
    ) : (
        <IconButton
            ariaLabel={localize(messages.toggleSearchBox)}
            onClick={handleSearchIconClick}
            styles={buttonStyles ?? { root: iconRootClassName, menuIcon: menuIconClassName }}
            menuIconProps={{ iconName: 'Search', ...menuIconProps }}
            data-automation-id="collapsableSearchIcon"
            disabled={props.disabled}
        />
    );
};
